/**
 * useDiagnosticsSummary — fetches diagnostics.events.summary from the plugin.
 *
 * @param {{ enabled: boolean }} options
 *   enabled — set to pluginSnapshot?.capabilities?.diagnosticEventsV1 === true
 *
 * Caches the last successful result for 60 s so re-renders don't spam the bridge.
 * On capability_missing from the plugin, behaves identically to enabled=false.
 *
 * @returns {{
 *   summary: NormalizedSummary|null,
 *   isLoading: boolean,
 *   error: string|null,
 * }}
 *
 * NormalizedSummary:
 * {
 *   highestSeverity: 'none'|'warning'|'error'|'critical',
 *   needsAttention: boolean,
 *   counts: { warning: number, error: number, critical: number },
 *   topEvents: Array<{ id, severity, scope, code, message, occurred_at }>,
 *   latestEventId: number|null,
 *   generatedAt: string,
 * }
 */

import { useState, useEffect, useRef } from 'react';
import { BridgeClient } from '../../../app/service/bridge/BridgeClient';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';

const CACHE_TTL_MS = 60_000;

function normalizeSummary(raw) {
  return {
    highestSeverity: raw.highest_severity || 'none',
    needsAttention: !!raw.needs_attention,
    counts: {
      warning: (raw.counts?.warning) || 0,
      error: (raw.counts?.error) || 0,
      critical: (raw.counts?.critical) || 0,
    },
    topEvents: Array.isArray(raw.top_events) ? raw.top_events : [],
    latestEventId: raw.latest_event_id ?? null,
    generatedAt: raw.generated_at || null,
  };
}

export function useDiagnosticsSummary({ enabled }) {
  const t = useLocalizedMessage();
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const bridgeRef = useRef(null);
  const cacheRef = useRef({ value: null, fetchedAt: 0 });

  useEffect(() => {
    if (!enabled) {
      setSummary(null);
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    const now = Date.now();
    if (cacheRef.current.value && (now - cacheRef.current.fetchedAt) < CACHE_TTL_MS) {
      setSummary(cacheRef.current.value);
      return undefined;
    }

    let cancelled = false;

    if (!bridgeRef.current) {
      bridgeRef.current = new BridgeClient();
    }

    setIsLoading(true);
    setError(null);

    bridgeRef.current
      .getDiagnosticsSummary({ window: '24h', severity_min: 'warning' })
      .then((response) => {
        if (cancelled) return;

        if (response.result === 'error' || response.error === 'capability_missing') {
          setSummary(null);
          setIsLoading(false);
          return;
        }

        const normalized = normalizeSummary(response);
        cacheRef.current = { value: normalized, fetchedAt: Date.now() };
        setSummary(normalized);
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || err?.error || t('operator.diag.summary.loadFailed'));
        setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [enabled, t]);

  return { summary, isLoading, error };
}
